import { FormEvent, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Button } from "@/components/ui/Button";
import {
  listSiengeColaboradores,
  type SiengeColaboradorOption,
} from "@/services/folhaColaboradores";

interface ModalColaboradorProps {
  open: boolean;
  onClose: () => void;
  onSave: (siengeId: number) => Promise<void>;
}

export function ModalColaborador({ open, onClose, onSave }: ModalColaboradorProps) {
  const [options, setOptions] = useState<SiengeColaboradorOption[]>([]);
  const [siengeId, setSiengeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSiengeId("");
    setError("");
    setSaving(false);
    setLoading(true);
    void listSiengeColaboradores()
      .then((list) => setOptions(list))
      .catch(() => {
        setOptions([]);
        setError("Não foi possível carregar os colaboradores do Sienge.");
      })
      .finally(() => setLoading(false));
  }, [open]);

  const selectOptions = useMemo(
    () =>
      options.map((item) => ({
        value: String(item.id),
        label: item.documentNumber
          ? `${item.name} — ${item.documentNumber}`
          : `${item.name} (ID ${item.id})`,
      })),
    [options],
  );

  const selected = options.find((item) => item.id === Number(siengeId)) ?? null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = Number(siengeId);
    if (!Number.isInteger(id) || id <= 0 || !selected) {
      setError("Selecione um colaborador do Sienge.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave(id);
      onClose();
    } catch (submitError) {
      const message =
        submitError &&
        typeof submitError === "object" &&
        "response" in submitError &&
        (submitError as { response?: { data?: { error?: string } } }).response?.data?.error
          ? String(
              (submitError as { response?: { data?: { error?: string } } }).response?.data?.error,
            )
          : "Não foi possível cadastrar o colaborador.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    if (!saving) onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Cadastrar colaborador"
      size="md"
      closeOnEscape
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="folha-colaborador-form" loading={saving}>
            Cadastrar colaborador
          </Button>
        </div>
      }
    >
      <form id="folha-colaborador-form" className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
        <p className="text-sm text-slate-500">
          Busque no Sienge os credores marcados como funcionário e vincule à folha de pagamentos.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <SearchableSelect
          label="Colaborador (Sienge)"
          options={selectOptions}
          value={siengeId}
          onChange={(value) => {
            setSiengeId(value);
            setError("");
          }}
          placeholder={loading ? "Carregando Sienge…" : "Digite o nome ou CPF…"}
          loading={loading}
          disabled={loading || saving}
        />

        {selected && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <p>
              <span className="font-medium text-slate-700">Nome: </span>
              {selected.name}
            </p>
            <p className="mt-1">
              <span className="font-medium text-slate-700">Documento: </span>
              {selected.documentNumber || "—"}
            </p>
            <p className="mt-1">
              <span className="font-medium text-slate-700">ID Sienge: </span>
              {selected.id}
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
}
